import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'

import { createClient } from '@/lib/api/supabase/client'
import { useCurrentUser } from '@/lib/hooks/auth/useCurrentUser'
import { useSupabaseAuth } from '@/lib/hooks/auth/useSupabaseAuth'
import logger from '@/utils/logger'

export const MANAGEMENT_TOPICS = {
  projects: 'mgmt:projects',
  materials: 'mgmt:materials',
  masters: 'mgmt:masters',
  users: 'mgmt:users',
} as const

const MANAGEMENT_CATEGORIES = ['projects', 'materials', 'masters', 'users'] as const

/**
 * 論理トピックを実際に購読する wire トピックへ変換する。
 * - カテゴリトピック（mgmt:projects 等）は会社スコープ `mgmt:{companyId}:{category}` になる。
 * - per-project トピック（mgmt:project:{id}）はそのまま（購読は RLS で会社照合される）。
 * - companyId 未取得時はカテゴリトピックを購読できないため null を返す。
 */
const resolveWireTopic = (
  topic: ManagementTopic,
  companyId: string | null | undefined,
): string | null => {
  if (topic.startsWith('mgmt:project:')) {
    return topic
  }
  const category = topic.slice('mgmt:'.length)
  if ((MANAGEMENT_CATEGORIES as readonly string[]).includes(category)) {
    return companyId ? `mgmt:${companyId}:${category}` : null
  }
  return topic
}

export type ManagementTopic =
  | (typeof MANAGEMENT_TOPICS)[keyof typeof MANAGEMENT_TOPICS]
  | `mgmt:project:${string}`

export interface ManagementLiveEvent {
  topic: ManagementTopic
  operation: 'INSERT' | 'UPDATE' | 'DELETE'
  payload: unknown
}

type ManagementLiveListener = (event: ManagementLiveEvent) => void

interface ManagementSyncContextValue {
  subscribe: (
    topics: readonly ManagementTopic[],
    listener: ManagementLiveListener,
  ) => () => void
}

const ManagementSyncContext = createContext<ManagementSyncContextValue | undefined>(
  undefined,
)

const BROADCAST_OPERATIONS = ['INSERT', 'UPDATE', 'DELETE'] as const
const MANAGEMENT_EVENT_DEBOUNCE_MS = 500
const realtimeEnabled =
  (import.meta.env.VITE_ENABLE_REALTIME_SYNC ?? 'false').trim().toLowerCase() ===
  'true'

const normalizeTopics = (
  topics: readonly (ManagementTopic | null | undefined)[],
): ManagementTopic[] => {
  const deduped = new Set<ManagementTopic>()
  for (const topic of topics) {
    if (topic) {
      deduped.add(topic)
    }
  }
  return Array.from(deduped)
}

export const getManagementProjectTopic = (projectId: string): ManagementTopic =>
  `mgmt:project:${projectId}`

function useEventCallback<T extends (...args: never[]) => void>(callback: T): T {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return useCallback(((...args: Parameters<T>) => callbackRef.current(...args)) as T, [])
}

export function ManagementSyncProvider({
  children,
}: {
  children: ReactNode
}): ReactNode {
  const supabase = useMemo(() => createClient(), [])
  const { session } = useSupabaseAuth()
  const { user: currentUser } = useCurrentUser()
  const companyId = currentUser?.company_id ?? null
  const listenersRef = useRef<Map<ManagementTopic, Set<ManagementLiveListener>>>(
    new Map(),
  )
  const channelsRef = useRef<Map<ManagementTopic, RealtimeChannel>>(new Map())
  const pendingEventsRef = useRef<Map<ManagementTopic, ManagementLiveEvent>>(
    new Map(),
  )
  const pendingTimersRef = useRef<
    Map<ManagementTopic, ReturnType<typeof setTimeout>>
  >(new Map())

  const flushTopic = useCallback((topic: ManagementTopic) => {
    const timer = pendingTimersRef.current.get(topic)
    if (timer) {
      clearTimeout(timer)
      pendingTimersRef.current.delete(topic)
    }

    const event = pendingEventsRef.current.get(topic)
    pendingEventsRef.current.delete(topic)
    if (!event) {
      return
    }

    const listeners = listenersRef.current.get(topic)
    if (!listeners || listeners.size === 0) {
      return
    }

    for (const listener of listeners) {
      listener(event)
    }
  }, [])

  const queueTopicEvent = useCallback(
    (topic: ManagementTopic, event: ManagementLiveEvent) => {
      pendingEventsRef.current.set(topic, event)
      if (pendingTimersRef.current.has(topic)) {
        return
      }

      const timer = setTimeout(() => {
        flushTopic(topic)
      }, MANAGEMENT_EVENT_DEBOUNCE_MS)
      pendingTimersRef.current.set(topic, timer)
    },
    [flushTopic],
  )

  const teardownChannel = useCallback((topic: ManagementTopic) => {
    const channel = channelsRef.current.get(topic)
    if (!channel) {
      return
    }

    void channel.unsubscribe()
    channelsRef.current.delete(topic)
  }, [])

  const teardownAllChannels = useCallback(() => {
    for (const topic of channelsRef.current.keys()) {
      teardownChannel(topic)
    }
  }, [teardownChannel])

  const ensureChannel = useCallback(
    (topic: ManagementTopic) => {
      if (!realtimeEnabled || !session?.access_token) {
        return
      }

      if (channelsRef.current.has(topic)) {
        return
      }

      // 会社スコープの wire トピックへ変換。companyId 未取得のカテゴリトピックは
      // 購読できない（null）ためスキップし、companyId 取得後に再試行される。
      const wireTopic = resolveWireTopic(topic, companyId)
      if (!wireTopic) {
        return
      }

      const channel = supabase.channel(wireTopic, {
        config: { private: true },
      })

      for (const operation of BROADCAST_OPERATIONS) {
        channel.on('broadcast', { event: operation }, (payload) => {
          queueTopicEvent(topic, {
            topic,
            operation,
            payload,
          })
        })
      }

      channel.subscribe((status) => {
        logger.debug('Management realtime channel status', {
          topic,
          wireTopic,
          status,
        })
      })
      channelsRef.current.set(topic, channel)
    },
    [companyId, queueTopicEvent, session?.access_token, supabase],
  )

  useEffect(() => {
    if (!realtimeEnabled || !session?.access_token) {
      teardownAllChannels()
      return
    }

    for (const topic of listenersRef.current.keys()) {
      ensureChannel(topic)
    }
  }, [ensureChannel, session?.access_token, teardownAllChannels])

  useEffect(() => {
    return () => {
      teardownAllChannels()
      for (const timer of pendingTimersRef.current.values()) {
        clearTimeout(timer)
      }
      pendingTimersRef.current.clear()
      pendingEventsRef.current.clear()
    }
  }, [teardownAllChannels])

  const subscribe = useCallback(
    (
      topics: readonly ManagementTopic[],
      listener: ManagementLiveListener,
    ) => {
      const normalizedTopics = normalizeTopics(topics)
      if (normalizedTopics.length === 0) {
        return () => undefined
      }

      for (const topic of normalizedTopics) {
        const listeners = listenersRef.current.get(topic) ?? new Set()
        listeners.add(listener)
        listenersRef.current.set(topic, listeners)
        ensureChannel(topic)
      }

      return () => {
        for (const topic of normalizedTopics) {
          const listeners = listenersRef.current.get(topic)
          if (!listeners) {
            continue
          }

          listeners.delete(listener)
          if (listeners.size === 0) {
            listenersRef.current.delete(topic)
            teardownChannel(topic)
          }
        }
      }
    },
    [ensureChannel, teardownChannel],
  )

  const value = useMemo<ManagementSyncContextValue>(
    () => ({ subscribe }),
    [subscribe],
  )

  return (
    <ManagementSyncContext.Provider value={value}>
      {children}
    </ManagementSyncContext.Provider>
  )
}

export function useManagementLiveSync(
  topics: readonly (ManagementTopic | null | undefined)[],
  onEvent: () => void,
): void {
  const context = useContext(ManagementSyncContext)
  const onEventEffect = useEventCallback(onEvent)
  const topicKey = normalizeTopics(topics).join('\u0000')
  const normalizedTopics = useMemo(
    () => (topicKey ? (topicKey.split('\u0000') as ManagementTopic[]) : []),
    [topicKey],
  )

  useEffect(() => {
    if (!context || normalizedTopics.length === 0) {
      return undefined
    }

    return context.subscribe(normalizedTopics, () => {
      onEventEffect()
    })
  }, [context, normalizedTopics, onEventEffect])
}
